import { Link } from "react-router-dom";
import { CALENDAR_URL } from "@/config";
import { fmtMoney } from "@/lib/format";

// Sharp Cut Sellers Side monogram
export const SSMark = ({ dark }) => (
  <svg viewBox="0 0 2001 2000" aria-hidden="true" className={`ss-mark ${dark ? "ss-mark--dark" : ""}`}>
    <path
      className="m"
      d="M1164.56,496.98l-603.68,159.89v254.54l947.4-251.32v70.95l-507.75,134.49,507.75,134.48h.05s0,395.6,0,395.6l-1015.59,268.98v-395.62l507.74-134.47-507.75-134.47h-.04v-395.63l671.88-177.94v70.53ZM560.92,1321.44v254.55l879.21-232.86v-254.54l-879.21,232.86ZM1508.27,660.09l-343.72-233.64,343.72-91.03v324.67Z"
    />
    <path className="a" d="M1508.32,660.09l-343.73-233.64,343.73-91.03v324.67Z" />
  </svg>
);

export const Eyebrow = ({ children, className = "", dark = false }) => (
  <p className={`ss-kicker ${dark ? "ss-kicker--dark" : ""} ${className}`}>{children}</p>
);

export const Wordmark = ({ dark }) => (
  <Link to="/" className="inline-flex items-center gap-3 no-underline" data-testid="wordmark">
    <SSMark dark={dark} />
    <span className="flex flex-col leading-none">
      <span className={`font-sans font-extrabold text-[17px] tracking-tight lowercase ${dark ? "text-white" : "text-[color:var(--ink)]"}`}>
        sellers side
      </span>
      <span className="ss-mono text-[11px] mt-1 tracking-[0.08em] text-[color:var(--grey)] lowercase">
        profit scorecard
      </span>
    </span>
  </Link>
);

export const TopBar = () => (
  <header className="bg-white border-b-2 border-[color:var(--ink)] sticky top-0 z-40">
    <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
      <Wordmark />
      <span className="hidden sm:inline ss-mono text-[11px] uppercase tracking-[0.18em] text-[color:var(--grey)]">
        Free scorecard · No obligation
      </span>
    </div>
  </header>
);

export const CtaBand = ({ cur, value, testId = "cta-band" }) => (
  <section className="ss-dark" data-testid={testId}>
    <div className="mx-auto max-w-4xl px-6 py-16 sm:py-24 text-center">
      <p className="ss-kicker ss-kicker--dark mb-6 justify-center">Next step</p>
      <h2 className="font-display text-3xl sm:text-5xl leading-[1.02] text-white">
        Get the 90-day plan to bank{" "}
        <span className="text-[color:var(--green-bright)]">{fmtMoney(cur, value)}/month</span>
      </h2>
      <p className="mt-5 text-white/70 text-base max-w-xl mx-auto">
        30-minute call. Every line above comes with the full term-level action list.
      </p>
      <a
        href={CALENDAR_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="ss-btn-primary ss-btn-primary-dark mt-8"
        data-testid={`${testId}-book-call-btn`}
      >
        Book a call
      </a>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="border-t-2 border-[color:var(--ink)] bg-white" data-testid="site-footer">
    <div className="mx-auto max-w-6xl px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <Wordmark />
      <div className="ss-mono text-xs text-[color:var(--grey)] sm:text-right space-y-1 uppercase tracking-[0.06em]">
        <p>
          <a
            href="https://sellersside.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[color:var(--ink)] underline underline-offset-4 decoration-2 decoration-[color:var(--green)]"
            data-testid="privacy-link"
          >
            Privacy policy
          </a>
        </p>
        <p>Reports processed in the EU · deleted after analysis · scorecards expire after 90 days.</p>
      </div>
    </div>
  </footer>
);

// KPI row — top 2px ink border, 1px hairline dividers between tiles.
export const MetricRow = ({ items, testId }) => (
  <div
    className="grid grid-cols-2 md:grid-cols-4 border-t-2 border-[color:var(--ink)] my-6 divide-x divide-[color:var(--hairline)]"
    style={{ gridTemplateColumns: `repeat(${Math.min(items.length, 4)}, minmax(0,1fr))` }}
    data-testid={testId}
  >
    {items.map((m, i) => (
      <div key={i} className="p-5">
        <p className="ss-mono text-[10px] uppercase tracking-[0.16em] text-[color:var(--grey)] mb-2">{m.label}</p>
        <p
          className="ss-mono text-2xl sm:text-3xl font-semibold tabular-nums"
          style={{ color: m.color || "#0d0d0d" }}
        >
          {m.value}
        </p>
        {m.sub && <p className="text-xs text-[color:var(--grey)] mt-2">{m.sub}</p>}
      </div>
    ))}
  </div>
);

// CSS bar pair — actual (green) vs benchmark (ink).
export const BarPair = ({ label, actual, bench, color = "#007d62", suffix = "%" }) => {
  const max = Math.max(actual || 0, bench || 0, 1) * 1.15;
  return (
    <div className="space-y-1.5">
      <p className="ss-mono text-[11px] uppercase tracking-[0.14em] text-[color:var(--ink)]">{label}</p>
      <div className="flex items-center gap-2">
        <div className="h-[14px] flex-1 bg-[color:var(--panel)] border border-[color:var(--hairline)]">
          <div className="h-full" style={{ width: `${((actual || 0) / max) * 100}%`, backgroundColor: color }} />
        </div>
        <span className="ss-mono text-xs w-14 text-right font-semibold" style={{ color }}>
          {actual == null ? "—" : `${actual}${suffix}`}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-[14px] flex-1 bg-[color:var(--panel)] border border-[color:var(--hairline)]">
          <div className="h-full bg-[color:var(--ink)]" style={{ width: `${((bench || 0) / max) * 100}%` }} />
        </div>
        <span className="ss-mono text-xs w-14 text-right text-[color:var(--grey)]">
          {bench == null ? "—" : `${bench}${suffix}`}
        </span>
      </div>
      <p className="ss-mono text-[10px] uppercase tracking-[0.12em] text-[color:var(--grey)] flex gap-3">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5" style={{ backgroundColor: color }} /> Yours
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 bg-[color:var(--ink)]" /> Benchmark
        </span>
      </p>
    </div>
  );
};
